import { useEffect, useState } from "react";
import { Link } from "wouter";
import { Button, Card, ClevisMark } from "@clevis/shared-ui";
import { call } from "../../lib/callFunction";

// Clevis Relay's landing page (app.clevis.pro/refer/{slug}/{code}): what a
// friend sees when a customer shares their link. In the company's name and
// words; one button on to the request form, which carries the code so the
// referral is tracked from here on.

interface Page {
  company: { name: string; logoUrl: string | null; phone: string };
  referrer: string;
  headline: string;
  blurb: string;
  friendOffer: string;
  requestUrl: string;
}

export function ReferPage({ slug, code }: { slug: string; code: string }) {
  const [page, setPage] = useState<Page | null | "missing">(null);
  useEffect(() => {
    call<Page>("getReferralPage", { slug, code }, { mutation: false })
      .then(setPage)
      .catch(() => setPage("missing"));
  }, [slug, code]);

  return (
    <div className="min-h-full bg-cream px-4 py-8">
      <div className="mx-auto max-w-lg space-y-4">
        {page === null ? (
          <Card>
            <p className="text-sm text-steel">Loading…</p>
          </Card>
        ) : page === "missing" ? (
          <Card>
            <p className="text-sm text-ink">This link isn't active.</p>
            <p className="mt-1 text-xs text-steel">Ask the person who sent it for a fresh one, or contact the company directly.</p>
          </Card>
        ) : (
          <Card className="space-y-4 p-6">
            <div className="flex items-center gap-3">
              {page.company.logoUrl ? <img src={page.company.logoUrl} alt="" className="h-12 w-12 rounded-lg object-contain" /> : <ClevisMark size={36} />}
              <div className="text-base font-bold text-ink">{page.company.name}</div>
            </div>
            <h1 className="text-2xl font-bold leading-tight text-ink">{page.headline}</h1>
            <p className="text-sm text-ink/80">{page.blurb}</p>
            {page.friendOffer ? <p className="rounded-lg bg-brandorange/10 px-3 py-2 text-sm font-semibold text-brandorange-dark">{page.friendOffer}</p> : null}
            <div className="flex flex-wrap items-center gap-3 pt-1">
              <Link href={page.requestUrl}>
                <Button type="button">Request service</Button>
              </Link>
              {page.company.phone ? (
                <a className="text-sm text-steel hover:text-ink" href={`tel:${page.company.phone.replace(/[^\d+]/g, "")}`}>
                  or call {page.company.phone}
                </a>
              ) : null}
            </div>
            <p className="text-[11px] text-steel">Sent by {page.referrer}. Your request goes straight to {page.company.name}.</p>
          </Card>
        )}
      </div>
    </div>
  );
}
